import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Paper, Grid, Autocomplete, TextField,
    Card, CardContent, Divider, Alert, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Chip,
    Backdrop, CircularProgress
} from '@mui/material';
import {
    CameraAlt as CameraIcon,
    Search as SearchIcon,
    FolderZip as ZipIcon,
    History as HistoryIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const AgentLauncherPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [allPatients, setAllPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);   const [backupHistory, setBackupHistory] = useState([]);
 
    useEffect(() => {
        const fetchPatients = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                const res = await fetch(`${API_URL}/api/patients`, { 
                    headers: { 'Authorization': `Bearer ${token}` } 
                });
                if (res.ok) setAllPatients(await res.json());
            } catch (e) { console.error(e); }
        };
        fetchPatients();
        loadHistory();
    }, []);

    // Cargar historial local (del navegador)
    const loadHistory = () => {
        const history = JSON.parse(localStorage.getItem('luiatro_backups') || '[]');
        setBackupHistory(history);
    };

    // Función para construir el link
    const buildProtocolUrl = (mode) => {
    if (!selectedPatient || !user) return '';
    const params = new URLSearchParams({
        mode: mode, 
        id: selectedPatient.id,
        name: `${selectedPatient.name} ${selectedPatient.lastName}`,
        // CAMBIO: Enviamos la cédula del paciente en lugar del ID del doctor
        docId: selectedPatient.cedula || 'S-C', 
        tenant: user.institutionId || 'General'
    });
    return `luiatro://open?${params.toString()}`;
	};

    const handleLaunchAgent = () => {
        // 1. Activar carga
        setIsProcessing(true);

        // 2. Usamos un pequeño timeout para dar tiempo a que aparezca el círculo antes de que el navegador intente abrir el programa
        setTimeout(() => {
            const url = buildProtocolUrl('capture');
            if(url) {
                window.location.href = url;
                
                // 3. Desactivar carga automáticamente después de 5 segundos
                // (Tiempo suficiente para que el usuario vea que "algo pasó" y el programa Python abra)
                setTimeout(() => setIsProcessing(false), 5000);
            } else {
                setIsProcessing(false);
            }
        }, 500);
    };

    const handleBackupZip = () => {
        setIsProcessing(true); // Activar carga

        setTimeout(() => {
            const url = buildProtocolUrl('backup');
            if(url) {
                window.location.href = url;
                
                // Guardar en historial
                const newEntry = {
                    date: new Date().toLocaleString(),
                    patient: `${selectedPatient.name} ${selectedPatient.lastName}`,
                    type: 'Respaldo ZIP Local',
                    status: 'Procesado'
                };
                const updatedHistory = [newEntry, ...backupHistory].slice(0, 50);
                localStorage.setItem('luiatro_backups', JSON.stringify(updatedHistory));
                setBackupHistory(updatedHistory);

                // Desactivar carga a los 5 segundos
                setTimeout(() => setIsProcessing(false), 5000);
            } else {
                setIsProcessing(false);
            }
        }, 500);
    };

    return (
        <Box sx={{ p: 4, maxWidth: '1400px', margin: '0 auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} variant="outlined" size="large">
                    Volver
                </Button>
                <Typography variant="h4" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CameraIcon fontSize="large" /> Agente de Captura & Respaldo
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* IZQUIERDA: CONTROLES */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={3} sx={{ p: 4, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">1. Seleccionar Paciente</Typography>
                        <Autocomplete
                            options={allPatients}
                            getOptionLabel={(p) => `${p.name} ${p.lastName} (${p.cedula || 'S/C'})`}
                            onChange={(_, v) => setSelectedPatient(v)}
                            renderInput={(params) => (
                                <TextField {...params} label="Buscar Paciente..." variant="outlined" fullWidth />
                            )}
                            sx={{ mb: 4 }}
                        />

                        {selectedPatient ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Card variant="outlined" sx={{ bgcolor: '#e3f2fd', border: '1px dashed #1976d2' }}>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>MODO CAPTURA</Typography>
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            size="large" 
                                            startIcon={<CameraIcon />}
                                            onClick={handleLaunchAgent}
                                            fullWidth
                                            sx={{ py: 2, fontSize: '1.1rem' }}
                                        >
                                            INICIAR CÁMARA
                                        </Button>
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                            Abre la cámara para tomar fotos y videos.
                                        </Typography>
                                    </CardContent>
                                </Card>

                                <Card variant="outlined" sx={{ bgcolor: '#f3e5f5', border: '1px dashed #8e44ad' }}>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="subtitle2" color="secondary" gutterBottom>MODO RESPALDO</Typography>
                                        <Button 
                                            variant="contained" 
                                            color="secondary" 
                                            size="large" 
                                            startIcon={<ZipIcon />}
                                            onClick={handleBackupZip}
                                            fullWidth
                                            sx={{ py: 2, fontSize: '1.1rem' }}
                                        >
                                            GENERAR ZIP
                                        </Button>
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                            Comprime todas las fotos/videos de este paciente en un ZIP.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        ) : (
                            <Alert severity="info">Seleccione un paciente para ver las opciones.</Alert>
                        )}
                    </Paper>
                </Grid>

                {/* DERECHA: HISTORIAL */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={3} sx={{ p: 4, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HistoryIcon /> Historial de Respaldos (Este Equipo)
                            </Typography>
                            <Button size="small" onClick={() => { localStorage.removeItem('luiatro_backups'); setBackupHistory([]); }}>Borrar Historial</Button>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        {backupHistory.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 5, color: '#999' }}>
                                <ZipIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                                <Typography>No hay registros de respaldos recientes.</Typography>
                            </Box>
                        ) : (
                            <TableContainer sx={{ flexGrow: 1, maxHeight: 400 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell>Paciente</TableCell>
                                            <TableCell>Estado</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {backupHistory.map((row, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell sx={{ fontSize: '0.85rem' }}>{row.date}</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{row.patient}</TableCell>
                                                <TableCell>
												   <Chip 
													   icon={<CheckIcon />} 
													   label={row.status || 'Enviado'} // <--- Ahora leerá el status del historial
													   color="success" 
													   size="small" 
													   variant="outlined" 
												   />
												</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>
			<Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 999,
                    flexDirection: 'column',
                    gap: 2
                }}
                open={isProcessing}
            >
                <CircularProgress color="inherit" size={60} />
                <Typography variant="h6">Conectando con el Agente...</Typography>
                <Typography variant="body2">Por favor, espere mientras se abre la aplicación externa.</Typography>
            </Backdrop>
        </Box>
    );
};

export default AgentLauncherPage;